import type { Locale } from "./types";

// UI strings for both languages. Kept flat and simple so it can later move
// to i18next resource files without changing the useI18n() consumer API.
type Dict = Record<string, string>;

export const ui: Record<Locale, Dict> = {
  es: {
    "nav.home": "Inicio",
    "nav.about": "Sobre mí",
    "nav.experience": "Experiencia",
    "nav.projects": "Proyectos",
    "nav.skills": "Skills",
    "nav.ai": "IA Aplicada",
    "nav.process": "Proceso",
    "nav.contact": "Contacto",
    "nav.cta": "Ver proyectos",

    "hero.eyebrow": "Disponible para nuevos proyectos",
    "hero.title": "Senior Frontend & Full-Stack Developer",
    "hero.subtitle":
      "Construyo interfaces modernas, plataformas internas, dashboards y soluciones con IA aplicada para resolver problemas reales de negocio.",
    "hero.secondary":
      "Especializado en React, TypeScript, Laravel, FastAPI, SQL Server, PostgreSQL y productos SaaS con enfoque en UX, automatización e inteligencia artificial.",
    "hero.cta.projects": "Ver proyectos",
    "hero.cta.cv": "Descargar CV",

    "about.tag": "Sobre mí",
    "about.title": "Desarrollador senior con visión de producto",
    "about.p1":
      "Soy desarrollador frontend senior con sólida experiencia full-stack. Combino frontend, backend, bases de datos e inteligencia artificial aplicada para entregar productos completos.",
    "about.p2":
      "Mi enfoque está en productos reales: CRM, dashboards, cotizadores, automatización, modelos de predicción y asistentes inteligentes que mejoran procesos comerciales.",
    "about.stat1.value": "5+",
    "about.stat1.label": "Años construyendo productos",
    "about.stat2.value": "20+",
    "about.stat2.label": "Módulos y plataformas",
    "about.stat3.value": "IA",
    "about.stat3.label": "Aplicada a negocio real",

    "exp.tag": "Trayectoria",
    "exp.title": "Experiencia profesional",

    "projects.tag": "Portafolio",
    "projects.title": "Proyectos destacados",
    "projects.subtitle": "Selección de trabajos que combinan frontend, backend e IA aplicada.",
    "projects.filter.all": "Todos",
    "projects.filter.frontend": "Frontend",
    "projects.filter.fullstack": "Full-Stack",
    "projects.filter.ai": "IA",
    "projects.filter.backend": "Backend",
    "projects.detail": "Ver detalle",
    "projects.empty": "No hay proyectos en esta categoría.",

    "status.production": "En producción",
    "status.prototype": "Prototipo",
    "status.lab": "Laboratorio",
    "status.experimental": "Experimental",

    "skills.tag": "Stack",
    "skills.title": "Skills & tecnologías",
    "skills.subtitle": "Herramientas con las que trabajo a diario para llevar ideas a producción.",

    "ai.tag": "IA aplicada",
    "ai.title": "No solo uso IA: la integro en productos reales",
    "ai.subtitle":
      "No solo uso IA como herramienta de productividad; también la integro en productos reales con un enfoque práctico.",

    "process.tag": "Metodología",
    "process.title": "Proceso de trabajo",
    "process.subtitle": "Un camino claro desde el problema hasta el producto medible.",

    "contact.tag": "Contacto",
    "contact.title": "Construyamos algo juntos",
    "contact.subtitle": "¿Tienes un proyecto o una vacante? Estaré encantado de conversar.",
    "contact.email": "Contactar por correo",
    "contact.linkedin": "LinkedIn",
    "contact.github": "GitHub",
    "contact.portfolio": "Portfolio actual",

    "footer.rights": "© 2026 Daniel Camacho. Built with React & TypeScript.",
    "footer.built": "Diseñado y desarrollado con enfoque en UX e IA aplicada.",

    "lang.toggle": "EN",
    "theme.light": "Modo claro",
    "theme.dark": "Modo oscuro",
  },
  en: {
    "nav.home": "Home",
    "nav.about": "About",
    "nav.experience": "Experience",
    "nav.projects": "Projects",
    "nav.skills": "Skills",
    "nav.ai": "Applied AI",
    "nav.process": "Process",
    "nav.contact": "Contact",
    "nav.cta": "View projects",

    "hero.eyebrow": "Available for new projects",
    "hero.title": "Senior Frontend & Full-Stack Developer",
    "hero.subtitle":
      "I build modern interfaces, internal platforms, dashboards and applied-AI solutions to solve real business problems.",
    "hero.secondary":
      "Specialized in React, TypeScript, Laravel, FastAPI, SQL Server, PostgreSQL and SaaS products with a focus on UX, automation and artificial intelligence.",
    "hero.cta.projects": "View projects",
    "hero.cta.cv": "Download CV",

    "about.tag": "About me",
    "about.title": "A senior developer with product vision",
    "about.p1":
      "I'm a senior frontend developer with solid full-stack experience. I combine frontend, backend, databases and applied AI to deliver complete products.",
    "about.p2":
      "My focus is on real products: CRM, dashboards, quoting tools, automation, predictive models and intelligent assistants that improve commercial processes.",
    "about.stat1.value": "5+",
    "about.stat1.label": "Years building products",
    "about.stat2.value": "20+",
    "about.stat2.label": "Modules & platforms",
    "about.stat3.value": "AI",
    "about.stat3.label": "Applied to real business",

    "exp.tag": "Career",
    "exp.title": "Professional experience",

    "projects.tag": "Portfolio",
    "projects.title": "Featured projects",
    "projects.subtitle": "A selection of work combining frontend, backend and applied AI.",
    "projects.filter.all": "All",
    "projects.filter.frontend": "Frontend",
    "projects.filter.fullstack": "Full-Stack",
    "projects.filter.ai": "AI",
    "projects.filter.backend": "Backend",
    "projects.detail": "View detail",
    "projects.empty": "No projects in this category.",

    "status.production": "In production",
    "status.prototype": "Prototype",
    "status.lab": "Lab",
    "status.experimental": "Experimental",

    "skills.tag": "Stack",
    "skills.title": "Skills & technologies",
    "skills.subtitle": "The tools I work with daily to bring ideas into production.",

    "ai.tag": "Applied AI",
    "ai.title": "I don't just use AI: I ship it in real products",
    "ai.subtitle":
      "I don't just use AI as a productivity tool; I also integrate it into real products with a practical approach.",

    "process.tag": "Methodology",
    "process.title": "How I work",
    "process.subtitle": "A clear path from the problem to a measurable product.",

    "contact.tag": "Contact",
    "contact.title": "Let's build something together",
    "contact.subtitle": "Have a project or a role in mind? I'd be glad to talk.",
    "contact.email": "Email me",
    "contact.linkedin": "LinkedIn",
    "contact.github": "GitHub",
    "contact.portfolio": "Current portfolio",

    "footer.rights": "© 2026 Daniel Camacho. Built with React & TypeScript.",
    "footer.built": "Designed and developed with a focus on UX and applied AI.",

    "lang.toggle": "ES",
    "theme.light": "Light mode",
    "theme.dark": "Dark mode",
  },
};
